/**
 * The bowl, made rather than downloaded.
 *
 * A recording would have meant shipping a megabyte of audio to every patient
 * on a phone connection, choosing a licence for it, and having it arrive late
 * on the first ring because nothing had fetched it yet. A struck singing bowl
 * is a handful of inharmonic partials over a soft mallet thump, which the Web
 * Audio API makes directly, in a few hundred bytes of code, with no network
 * at all.
 *
 * What makes it read as a bowl rather than as a beep — or as the flat metal
 * gong this started out as — is four things. The partials are *not* whole
 * multiples of the fundamental: a bowl's modes sit near 1 : 2.7 : 5.2 : 8.4,
 * which is why the tone has no octave in it and never sounds like an organ.
 * Each partial dies at its own rate, the high ones first, so the sound
 * darkens as it fades the way real bronze does. Each is played by two
 * oscillators a few cents apart, and the slow pulse of the two drifting
 * against each other is the wobble that says "bowl" before anything else
 * does — every bowl is slightly out of round, and this is what that asymmetry
 * sounds like. And the mallet is felt rather than heard: a short, soft, low
 * thump instead of the bright crack of metal on metal.
 */

/**
 * The bowl's note. Medium bowls sit between roughly 180 and 320 Hz; this is
 * an A3, low enough to feel settled and high enough to carry through a laptop
 * speaker, which an 80 Hz gong does not.
 */
const FUNDAMENTAL_HZ = 220;

type GongPartial = {
    /** Multiple of the fundamental. A bowl's modes, not a harmonic series. */
    ratio: number;
    /** Loudness relative to the fundamental. */
    gain: number;
    /** Seconds until this partial is inaudible. */
    decay: number;
};

const PARTIALS: readonly GongPartial[] = [
    { ratio: 1, gain: 1, decay: 16 },
    { ratio: 2.74, gain: 0.5, decay: 11 },
    { ratio: 5.18, gain: 0.22, decay: 7 },
    { ratio: 8.41, gain: 0.1, decay: 4 },
    { ratio: 12.3, gain: 0.05, decay: 2.2 },
];

/**
 * The pair of voices per partial, in cents.
 *
 * At the fundamental this is about a 2 Hz beat — the slow breathing pulse a
 * bowl has and a synthesiser does not. Wider and it turns into two notes;
 * narrower and the tail goes dead flat.
 */
const BEAT_CENTS = 8;

/** How long the bowl rings. Everything is scheduled inside this. */
const TAIL_S = 16;

/**
 * The mallet: soft, low, and over before the tone has established. A wooden
 * striker on bronze is a thump you feel under the note, not the bright crack
 * a metal beater makes.
 */
const STRIKE_S = 0.09;
const STRIKE_HZ = 620;
const STRIKE_GAIN = 0.1;

/**
 * Not silence, but never louder than the person talking. A bowl that makes
 * someone tear their headphones off is a bowl nobody lets you use twice.
 */
const PEAK_GAIN = 0.3;

/**
 * Slower than a struck gong's. A bowl takes a moment to speak, and an instant
 * attack is the single thing that makes a synthesised one sound like a bell.
 */
const ATTACK_S = 0.02;

/** Web Audio refuses an exponential ramp to zero; this is inaudible. */
const SILENCE = 0.0001;

export type GongPlayer = {
    /** Sound it here. Local playback only — nothing reaches the mic track. */
    ring: () => void;
    /** Give the audio hardware back when the room closes. */
    close: () => void;
};

type AudioContextConstructor = new () => AudioContext;

function audioContextConstructor(): AudioContextConstructor | null {
    if (typeof window === "undefined") return null;
    const candidate =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: AudioContextConstructor })
            .webkitAudioContext;
    return candidate ?? null;
}

function strikeBuffer(context: AudioContext): AudioBuffer {
    const frames = Math.max(1, Math.floor(context.sampleRate * STRIKE_S));
    const buffer = context.createBuffer(1, frames, context.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let frame = 0; frame < frames; frame += 1) {
        samples[frame] = Math.random() * 2 - 1;
    }
    return buffer;
}

/**
 * A gong bound to one audio context, for the life of one room.
 *
 * Returns `null` where the browser has no Web Audio at all — the room still
 * works, it just has no gong, which is the right way round.
 *
 * The context is built on the first ring rather than up front: a suspended
 * context left lying around for a whole session is a warning in every browser
 * console, and by the time anyone rings, joining the call has long since
 * given the page the gesture the autoplay policy asks for. `resume()` is
 * called anyway, because a patient who has not touched their tab since
 * joining may have had it suspended underneath them.
 */
export function createGongPlayer(): GongPlayer | null {
    const Constructor = audioContextConstructor();
    if (!Constructor) return null;

    let context: AudioContext | null = null;

    const ring = () => {
        if (!context) context = new Constructor();
        if (context.state === "suspended") void context.resume();

        const now = context.currentTime;

        const master = context.createGain();
        master.gain.value = PEAK_GAIN;
        master.connect(context.destination);

        /**
         * The whole sound darkens as it dies. Letting the partials fade on
         * their own leaves the tail thin and glassy; sweeping the top off it
         * is what makes it sound like something with mass. It starts lower
         * than a gong's would: a bowl is never bright, even at the strike.
         */
        const body = context.createBiquadFilter();
        body.type = "lowpass";
        body.frequency.setValueAtTime(3000, now);
        body.frequency.exponentialRampToValueAtTime(600, now + TAIL_S);
        body.connect(master);

        for (const partial of PARTIALS) {
            for (const detune of [-BEAT_CENTS, BEAT_CENTS]) {
                const oscillator = context.createOscillator();
                oscillator.type = "sine";
                oscillator.frequency.value = FUNDAMENTAL_HZ * partial.ratio;
                oscillator.detune.value = detune;

                const envelope = context.createGain();
                envelope.gain.setValueAtTime(0, now);
                envelope.gain.linearRampToValueAtTime(partial.gain / 2, now + ATTACK_S);
                envelope.gain.exponentialRampToValueAtTime(
                    SILENCE,
                    now + partial.decay
                );

                oscillator.connect(envelope);
                envelope.connect(body);
                oscillator.start(now);
                oscillator.stop(now + partial.decay + 0.1);
            }
        }

        const strike = context.createBufferSource();
        strike.buffer = strikeBuffer(context);

        const strikeTone = context.createBiquadFilter();
        strikeTone.type = "bandpass";
        strikeTone.frequency.value = STRIKE_HZ;
        strikeTone.Q.value = 0.5;

        const strikeEnvelope = context.createGain();
        strikeEnvelope.gain.setValueAtTime(STRIKE_GAIN, now);
        strikeEnvelope.gain.exponentialRampToValueAtTime(SILENCE, now + STRIKE_S);

        strike.connect(strikeTone);
        strikeTone.connect(strikeEnvelope);
        strikeEnvelope.connect(body);
        strike.start(now);
        strike.stop(now + STRIKE_S);
    };

    const close = () => {
        const open = context;
        context = null;
        if (open && open.state !== "closed") void open.close();
    };

    return { ring, close };
}
